import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { gsap, Power3 } from 'gsap'
import { ChevronDown } from '@styled-icons/boxicons-regular'

import CategoryNav from 'src/components/Navbar/CategoryNav'
import HomeButton from 'src/components/shared/HomeButton'
import { H1, LargeP, MonoP, InternalLink } from 'src/components/shared/text'
import { media, getScreenType } from 'src/utils/style-helper'

const Nav = styled.nav`
  background-color: #f9f9f9;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 100;
  grid-template-columns: 1fr;
  overflow: hidden;
  ${media.mobile`
    padding: 30px 14px 20px 14px;
  `}
  ${media.tablet`
    padding: 40px 30px 20px 30px;
  `}
  ${media.desktop`
    padding: 30px 40px 30px 40px;
  `}
`

const Content = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  color: ${(props) => props.theme.colors.navy};
  margin: 0 auto;
  transition: 500ms all linear;
  ${media.mobile`
    justify-content: center;
    align-items: flex-end;
  `}
  ${media.desktop`
    flex-direction: ${(props) => (props.isBlog ? 'row' : 'column')};
    align-items: ${(props) => (props.isBlog ? 'flex-end' : 'center')};
    max-width: 1305px;
  `}
`

const SideKickText = styled(H1)`
  font-family: ${(props) => props.theme.fonts.athletics.medium};
  width: fit-content;
  transition: 200ms font-size ease-in-out;
  ${media.mobile`
    font-size: 40px;
  `}
  ${media.tablet`
    font-size: 75px;
  `}
  ${media.desktop`
    font-size: ${(props) => (props.isBlog ? 52 : 133)}px;
  `}
`

const ByFinnText = styled(LargeP)`
  ${media.mobile`
    margin: 0 0 1px 9px;
  `}
  ${media.tablet`
    margin: 0 0 1px 9px;
    padding-bottom: 9px;
  `}
  ${media.desktop`
    font-size: ${(props) => (props.isBlog ? '16px' : '24px')};
    margin: ${(props) => (props.isBlog ? '0 0 0 12px' : '-12px 0 0 0')};
    padding-bottom: 0;
  `}
`

const CategoryNavWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  height: 0;
`

const Categories = styled(MonoP)`
  color: ${(props) => props.theme.colors.navy};
  font-size: 16px;
  position: absolute;
  transform: translate(0, -50%);
  top: 40%;
  z-index: 100;
  cursor: pointer;
  &:hover {
    color: ${(props) => props.theme.colors.orange};
  }
  ${media.tablet`
    right: 0;
  `}
  ${media.desktop`
    right: 5%;
  `}
`

const CategoryIcon = styled(ChevronDown)`
  transform: ${(props) => (props.open ? 'rotate(180deg)' : 'none')};
  transition: 200ms transform ease-in-out;
  height: 28px;
  width: 20px;
  margin-top: -4px;
`

const Navbar = ({ location }) => {
  const isHomepage = location && (location.pathname === '/' || location.pathname === '/blog/')
  const isBlog = location && location.pathname.includes('articles')
  const isMobile = getScreenType() === 'mobile'

  let initialState = true
  if (isBlog && !isMobile) initialState = false
  const [open, setOpen] = useState(initialState)
  const [isAnimating, setIsAnimating] = useState(false)

  const toggleCategoriesNav = () => {
    if (!open) handleOpen()
    else handleClose()
  }

  const handleOpen = React.useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true)
      gsap.to('.Navbar_CategoryNav', {
        duration: 0.5,
        height: isMobile ? 'fit-content' : 80,
        visibility: 'visible',
        ease: Power3.easeOut,
      })
      setOpen(true)
      setIsAnimating(false)
    }
  }, [isAnimating, isMobile])

  const handleClose = React.useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true)
      gsap.to('.Navbar_CategoryNav', {
        visibility: 'hidden',
        height: 0,
        ease: Power3.easeOut,
        duration: 0.1,
      })
      setOpen(false)
      setIsAnimating(false)
    }
  }, [isAnimating])

  useEffect(() => {
    if ((isBlog && isMobile) || !isBlog) handleOpen()
    else handleClose()
  }, [isBlog, isMobile, handleOpen, handleClose])

  return (
    <Nav role="navigation" aria-label="main-navigation">
      <Content isBlog={isBlog}>
        {!isHomepage && <HomeButton top="37%" />}
        <SideKickText isBlog={isBlog}>
          <InternalLink name="Home" to="/">Sidekick</InternalLink>
        </SideKickText>
        <ByFinnText isBlog={isBlog}>by Finn</ByFinnText>
        {isBlog && !isMobile && (
          <Categories onClick={toggleCategoriesNav}>
            Categories
            <CategoryIcon open={open} />
          </Categories>
        )}
      </Content>
      <CategoryNavWrapper isBlog={isBlog} className="Navbar_CategoryNav">
        <CategoryNav />
      </CategoryNavWrapper>
    </Nav>
  )
}

export default Navbar
